import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGrip, faBookOpen, faBookReader, faSignOutAlt, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../layout/Logo';
import mokalengProPic from '../../utils/brandImages';

interface NavItem {
  label: string;
  href: string;
  icon: any;
}

interface ModeratorSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  isMobileOpen: boolean;
  setIsMobileOpen: (value: boolean) => void;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/moderator/dashboard', icon: faGrip },
  { label: 'My Courses', href: '/moderator/courses', icon: faBookOpen },
  { label: 'Library', href: '/moderator/library', icon: faBookReader },
];

export default function ModeratorSidebar({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }: ModeratorSidebarProps) {
  const { logout, user } = useAuth();
  const location = useLocation();

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <div
      className={`bg-white border-r border-canvas-300 flex flex-col h-screen fixed left-0 top-0 z-40 shadow-sm transition-all duration-300 w-64 ${
        isCollapsed ? 'md:w-20' : 'md:w-64'
      } ${isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
    >
      <div className="p-4 border-b border-canvas-200 flex items-center justify-between">
        {!isCollapsed && (
          <div className="flex-1">
            <Logo size="sm" asLink={false} />
            <span className="block text-[10px] font-bold uppercase tracking-wider text-brand-600 mt-1">Moderator</span>
          </div>
        )}

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden md:inline-flex p-1.5 hover:bg-canvas-100 rounded-lg transition-colors text-brand-900 flex-shrink-0"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <FontAwesomeIcon icon={faBars} className="h-5 w-5" />
        </button>

        <button
          onClick={() => setIsMobileOpen(false)}
          className="md:hidden p-1.5 hover:bg-canvas-100 rounded-lg transition-colors text-brand-900 flex-shrink-0"
          aria-label="Close menu"
        >
          <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={() => setIsMobileOpen(false)}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
              isActive(item.href) ? 'bg-brand-50 text-brand-700' : 'text-brand-900/70 hover:text-brand-900 hover:bg-canvas-100'
            }`}
            title={isCollapsed ? item.label : undefined}
          >
            <FontAwesomeIcon icon={item.icon} className="h-5 w-5 flex-shrink-0" />
            {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
          </Link>
        ))}
      </nav>

      <div className="border-t border-canvas-200 p-4 space-y-3">
        <div className={`flex items-center gap-3 px-2 ${isCollapsed ? 'justify-center' : 'mb-3'}`}>
          <div className="w-10 h-10 bg-brand-gradient rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img src={user?.profilePictureUrl || mokalengProPic} alt="Profile" className="w-full h-full object-cover" />
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-brand-950 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-brand-900/60 truncate">{user?.email}</p>
            </div>
          )}
        </div>

        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-brand-900/70 hover:text-brand-900 hover:bg-canvas-100 transition-all text-sm font-medium"
          title={isCollapsed ? 'Logout' : undefined}
        >
          <FontAwesomeIcon icon={faSignOutAlt} className="h-5 w-5 flex-shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}
